/**
 * The animated dot field in the background.
 *
 * The geometry comes straight from the Adobe XD source (`assets/dots.json`):
 * a 67 × 38 grid of circles, each with the opacity the designer gave it. On
 * top of that every dot slowly brightens and fades on its own random cycle,
 * and the whole field breathes a little, so the background feels alive
 * without ever leaving the design's palette.
 *
 * The page shows only a pale scattering of it (see `PAGE_LOOK`): one wide
 * field from the top-right corner that thins out towards the left edge
 * (`PAGE_MASK`), with a slow wave of light rolling out from the corner. On
 * phones the dots are closer together, faint and calm
 * (`PHONE_LOOK`, `PHONE_MASK`).
 *
 * The pointer touches the field lightly: where the mouse passes (or a finger
 * taps), the dots nearby brighten a little and fade back. Nothing moves; it
 * is meant to be felt rather than seen.
 */

import data from '@/assets/dots.json';

export interface DotFieldData {
  readonly width: number;
  readonly height: number;
  readonly pitch: number;
  readonly radius: number;
  readonly x0: number;
  readonly y0: number;
  readonly opacity: readonly (readonly number[])[];
}

export interface Dot {
  readonly x: number;
  readonly y: number;
  /** Opacity in the design, 0–1. */
  readonly base: number;
  /** Where in its cycle the dot starts, 0–2π. */
  readonly phase: number;
  /** Seconds per brighten-and-fade cycle. */
  readonly period: number;
  /** Where the slow wave reaches the dot: its distance from the top-right corner, in radians of the wave. */
  readonly ripple: number;
  /** How much of the dot the corner clusters let through, 0–1 (1 when no mask is given). */
  readonly mask?: number;
}

export const DOT_FIELD: DotFieldData = data;

/** How far a dot swings around its design opacity on its own (±), unless a look says otherwise. */
export const TWINKLE_DEPTH = 0.7;
/** Seconds for one breath of the whole field. */
export const BREATH_PERIOD = 9;
/** How much the whole field breathes (±). */
export const BREATH_DEPTH = 0.12;
/** Seconds for one slow wave to roll out from the top-right corner. */
export const WAVE_PERIOD = 8;
/** Length of one wave, in grid steps (about 250 px on a desktop, 150 px on a phone). */
export const WAVE_LENGTH = 14;
/** Dot radius against the design: since 0.22.4 1.09 (1.1 in 0.22.3, 0.95 in 0.22.0, 0.8 before), the grid unchanged. */
export const DOT_SIZE = 1.09;
/** Seconds per brighten-and-fade cycle of one dot: each dot picks its own within this range. */
export const PERIOD_MIN = 2.2;
export const PERIOD_MAX = 5.6;
const TAU = Math.PI * 2;

/**
 * How the field looks and moves: how many of the design's dots are shown and
 * how strong, how much each dot twinkles on its own and how deep the slow
 * wave of light runs through them.
 */
export interface DotLook {
  /** Share of dots kept, 0–1 (the rest are left out at random, the same ones on every visit). */
  readonly density: number;
  /** Factor on every dot's opacity. */
  readonly strength: number;
  /** ± swing of each dot on its own cycle. */
  readonly twinkle: number;
  /** ± swing of the wave that rolls out from the top-right corner. */
  readonly wave: number;
}

/** Desktop and tablet: a pale scattering on the right, gently twinkling, with a soft wave. */
export const PAGE_LOOK: DotLook = { density: 0.62, strength: 0.5, twinkle: 0.5, wave: 0.25 };
/**
 * Phone: the screen is narrow and the card covers most of it, so the dots are
 * a little denser but faint and calm — a slow, soft wave and little twinkle.
 */
export const PHONE_LOOK: DotLook = { density: 0.8, strength: 0.55, twinkle: 0.25, wave: 0.3 };

export function dotLook(viewportWidth: number): DotLook {
  return viewportWidth <= 560 ? PHONE_LOOK : PAGE_LOOK;
}

/**
 * One soft spot of dots: an ellipse centred at (`cx`, `cy`) with radii
 * `rx` × `ry`, all as shares of the screen, fading along `stops` — pairs of
 * (distance from the centre, 0 at the centre and 1 at the edge; how much shows).
 */
interface MaskSpot {
  readonly cx: number;
  readonly cy: number;
  readonly rx: number;
  readonly ry: number;
  readonly stops: readonly (readonly [number, number])[];
}
export type DotMask = readonly MaskSpot[];

/** Desktop and tablet: one wide field from the top-right corner, thinning out towards the left edge; none at the bottom left. */
export const PAGE_MASK: DotMask = [{ cx: 1, cy: 0, rx: 0.9, ry: 0.8, stops: [[0, 1], [0.4, 0.65], [1, 0]] }];
/** Phone: across the whole top and down the sides, and a trace at the bottom left. */
export const PHONE_MASK: DotMask = [
  { cx: 1, cy: 0, rx: 1.3, ry: 0.46, stops: [[0, 1], [0.45, 0.75], [1, 0]] },
  { cx: 0, cy: 1, rx: 0.8, ry: 0.3, stops: [[0, 0.7], [1, 0]] },
];

export function dotMask(viewportWidth: number): DotMask {
  return viewportWidth <= 560 ? PHONE_MASK : PAGE_MASK;
}

function spotWeight(spot: MaskSpot, x: number, y: number, width: number, height: number): number {
  const t = Math.hypot((x - spot.cx * width) / (spot.rx * width), (y - spot.cy * height) / (spot.ry * height));
  let previous: readonly [number, number] | undefined;
  for (const stop of spot.stops) {
    if (t <= stop[0]) {
      if (!previous) return stop[1];
      const share = (t - previous[0]) / (stop[0] - previous[0]);
      return previous[1] + (stop[1] - previous[1]) * share;
    }
    previous = stop;
  }
  return previous?.[1] ?? 0;
}

/** How much of a dot at (`x`, `y`) shows through the spots, 0–1; overlapping spots add up the way stacked masks do. */
export function maskWeight(x: number, y: number, width: number, height: number, mask: DotMask): number {
  let shown = 0;
  for (const spot of mask) {
    const weight = Math.min(1, Math.max(0, spotWeight(spot, x, y, width, height)));
    shown += weight * (1 - shown);
  }
  return shown;
}

/** Small deterministic PRNG (mulberry32) so the field looks the same on every visit. */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The dots for a field `width` × `height` pixels, drawn at `scale` (smaller
 * dots and a tighter grid on small screens). The design's grid is anchored to
 * the top-right corner exactly as in the XD file and repeats leftwards and
 * downwards with the designer's own opacities, so it fills any screen without
 * a seam; `look` thins it out and tones it down.
 */
export function buildDots(
  field: DotFieldData = DOT_FIELD,
  seed = 2026,
  width = field.width,
  height = field.height,
  scale = 1,
  look: Partial<Pick<DotLook, 'density' | 'strength'>> = {},
  mask?: DotMask,
): readonly Dot[] {
  const random = seededRandom(seed);
  const pick = seededRandom(seed ^ 0x5eed);
  const { density = 1, strength = 1 } = look;
  const dots: Dot[] = [];
  const pitch = field.pitch * scale;
  const radius = field.radius * scale;
  const rows = field.opacity.length;
  const cols = field.opacity[0]?.length ?? 0;
  if (rows === 0 || cols === 0) return dots;
  const lastX = field.x0 + (cols - 1) * field.pitch;
  const rightGap = (field.width - lastX) * scale;
  const waveLength = WAVE_LENGTH * pitch;
  for (let j = 0; ; j += 1) {
    const y = (field.y0 + j * field.pitch) * scale;
    if (y - radius >= height) break;
    const row = field.opacity[j % rows] ?? [];
    for (let k = 0; ; k += 1) {
      const x = width - rightGap - k * pitch;
      if (x + radius <= 0) break;
      const base = (row[cols - 1 - (k % cols)] ?? 0) * strength;
      if (base <= 0 || pick() >= density) continue;
      const ripple = (TAU * Math.hypot(width - x, y)) / waveLength;
      const dot: Dot = { x, y, base, phase: random() * TAU, period: PERIOD_MIN + random() * (PERIOD_MAX - PERIOD_MIN), ripple };
      dots.push(mask ? { ...dot, mask: maskWeight(x, y, width, height, mask) } : dot);
    }
  }
  return dots;
}

/**
 * How big a dot is against the design: on phones a touch bigger than the grid
 * shrinks (see `dotScale`), so the dots read as dots on a small, sharp screen
 * — about 3.5 px across every 10 px, the desktop's proportion (since 0.22.2;
 * before, 2.4 px every 8 px looked like dust).
 */
export function dotRadiusScale(viewportWidth: number): number {
  if (viewportWidth <= 560) return 0.62;
  return dotScale(viewportWidth);
}

/** The grid is a little tighter than the design's, and tighter still on tablets and phones. */
export function dotScale(viewportWidth: number): number {
  if (viewportWidth <= 560) return 0.56;
  if (viewportWidth <= 900) return 0.7;
  return 0.86;
}

export type DotMotion = Pick<DotLook, 'twinkle' | 'wave'>;
const STILL_WAVE: DotMotion = { twinkle: TWINKLE_DEPTH, wave: 0 };

/** A light touch of the pointer: where it passed and when, in seconds of the animation. */
export interface Gust {
  readonly x: number;
  readonly y: number;
  readonly start: number;
}
/** Seconds a touch takes to fade. */
export const GUST_LIFE = 1.8;
/** Seconds a touch takes to come up, eased, before it fades (since 0.22.4: before, it lit at once). */
export const GUST_RISE = 0.18;
/** How far a touch reaches, px at full size; it widens by half as it fades. */
export const GUST_RADIUS = 110;
/** How much a touch brightens the dots it reaches, 0–1. */
export const GUST_GLOW = 0.24;
/** Most touches kept at once; older ones give way. */
export const GUST_MAX = 12;

/**
 * How much the pointer brightens a dot at `seconds`, 0–GUST_GLOW: each touch
 * lights the dots around where the pointer passed, palely: it comes up over
 * GUST_RISE and fades with distance and with time. `scale` shrinks the reach on smaller screens.
 */
export function gustGlow(dot: Pick<Dot, 'x' | 'y'>, gusts: readonly Gust[], seconds: number, scale = 1): number {
  let dark = 1;
  for (const gust of gusts) {
    const age = seconds - gust.start;
    if (age < 0 || age >= GUST_LIFE) continue;
    const life = age / GUST_LIFE;
    const reach = GUST_RADIUS * scale * (1 + 0.5 * life);
    const distance = Math.hypot(dot.x - gust.x, dot.y - gust.y);
    if (distance > reach * 2.2) continue;
    // comes up softly (smoothstep), then fades out
    const rise = Math.min(1, age / GUST_RISE);
    const envelope = age < GUST_RISE ? rise * rise * (3 - 2 * rise) : (1 - (age - GUST_RISE) / (GUST_LIFE - GUST_RISE)) ** 2;
    dark *= 1 - Math.exp(-((distance / reach) ** 2)) * envelope * GUST_GLOW;
  }
  return 1 - dark;
}

/**
 * The opacity of one dot at `seconds` into the animation, clamped to 0–1:
 * its own twinkle, the wave rolling out from the top-right corner (crests
 * move away from the corner as time goes on) and the breath of the field,
 * all through the corner mask; `glow` from the pointer lights the dot a
 * little on top — fully in the clusters, faintly where the mask hides it.
 */
export function dotOpacity(dot: Dot, seconds: number, motion: DotMotion = STILL_WAVE, glow = 0): number {
  const twinkle = 1 + motion.twinkle * Math.sin(TAU * (seconds / dot.period) + dot.phase);
  const wave = 1 + motion.wave * Math.sin(TAU * (seconds / WAVE_PERIOD) - dot.ripple);
  const breath = 1 + BREATH_DEPTH * Math.sin(TAU * (seconds / BREATH_PERIOD));
  const shown = dot.base * (dot.mask ?? 1) * twinkle * wave * breath;
  // the pointer lights the dots anywhere on the screen, down to the bottom: where the mask hides them somewhat less than in the field (since 0.22.3; before, barely at all)
  const lit = glow > 0 ? glow * (0.4 + 0.6 * dot.base) * (0.6 + 0.4 * (dot.mask ?? 1)) : 0;
  return Math.min(1, Math.max(0, shown + lit));
}

export interface PaintOptions {
  readonly radius: number;
  readonly color: string;
  /** Seconds into the animation; null paints the design as drawn, through the mask, with no motion. */
  readonly seconds: number | null;
  readonly width?: number;
  readonly height?: number;
  readonly motion?: DotMotion;
  readonly gusts?: readonly Gust[];
  /** Size of the dots against the design, for the gusts. */
  readonly scale?: number;
}

/** Alpha under which a dot is not worth drawing. */
const INVISIBLE = 0.008;

/** Paints one frame. */
export function paintDots(ctx: CanvasRenderingContext2D, dots: readonly Dot[], options: PaintOptions): void {
  const { radius, color, seconds, width = DOT_FIELD.width, height = DOT_FIELD.height, motion = STILL_WAVE, gusts = [], scale = 1 } = options;
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = color;
  for (const dot of dots) {
    const alpha = seconds === null ? dot.base * (dot.mask ?? 1) : dotOpacity(dot, seconds, motion, gusts.length > 0 ? gustGlow(dot, gusts, seconds, scale) : 0);
    if (alpha < INVISIBLE) continue;
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.arc(dot.x, dot.y, radius, 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}
